import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Users,
  UserCheck,
  UserX,
  TrendingUp,
  GraduationCap,
  Clock,
  AlertTriangle,
  Database,
  Radio,
  ArrowRight,
  CalendarRange,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { DashboardSkeleton } from "@/components/skeletons/PageSkeletons";

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "custom", label: "Custom range" },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function StatBlock({ label, value, sub, icon: Icon, testid, accent = "text-[#C8102E]" }) {
  return (
    <div className="border-r border-b border-[#E2E8F0] p-6 bg-white" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">{label}</div>
        <Icon className={`w-4 h-4 shrink-0 ${accent}`} />
      </div>
      <div className="font-heading text-3xl font-extrabold tracking-tighter text-[#0F172A]">{value}</div>
      {sub && <div className="text-xs text-[#475569] mt-1.5">{sub}</div>}
    </div>
  );
}

function OverviewCard({ label, pct, present, total, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-5" data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">{label}</div>
      <div className="font-heading text-3xl font-extrabold tracking-tighter text-[#0F172A] mt-2">{pct}%</div>
      <div className="text-sm text-[#475569] mt-1">{present}/{total} present</div>
      <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden mt-3">
        <div className="h-full bg-[#C8102E]" style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

function pctBarColor(pct) {
  if (pct >= 90) return "#059669";
  if (pct >= 75) return "#C8102E";
  if (pct >= 60) return "#D97706";
  return "#E11D48";
}

function pctTextColor(pct) {
  if (pct >= 90) return "text-[#059669]";
  if (pct >= 75) return "text-[#C8102E]";
  if (pct >= 60) return "text-[#D97706]";
  return "text-[#E11D48]";
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [queryParams, setQueryParams] = useState({ period: "today" });
  const navigate = useNavigate();

  const loadStats = useCallback(async (params) => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats", { params });
      setStats(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats(queryParams);
  }, [loadStats, queryParams]);

  const selectPreset = (key) => {
    setPreset(key);
    if (key === "today") setQueryParams({ period: "today" });
    else if (key === "yesterday") setQueryParams({ period: "yesterday" });
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) return;
    setQueryParams({ date_from: customFrom, date_to: customTo });
  };

  const filterLabel = stats?.filter?.label ?? "Today";
  const isSingleDay = stats?.filter?.date_from === stats?.filter?.date_to;
  const periodLabel = isSingleDay ? "Selected day" : "Selected period";
  const trendLabel = isSingleDay
    ? `Attendance for ${stats?.filter?.date_from ?? ""}`
    : `Daily attendance % — ${stats?.filter?.date_from ?? ""} to ${stats?.filter?.date_to ?? ""}`;

  return (
    <div className="min-h-screen pb-16">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8] flex items-center gap-2">
          <CalendarRange className="w-3.5 h-3.5" />
          {filterLabel}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0F172A] mt-2" data-testid="dashboard-heading">
          Stats dashboard.
        </h1>
        <p className="text-[#475569] mt-3 max-w-2xl">
          KPIs, attendance trends, and section-wise breakdown from live roll-calls and camera sessions.
        </p>

        <div className="mt-6 flex flex-col gap-4">
          <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-date-filters">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => selectPreset(p.key)}
                data-testid={`dashboard-filter-${p.key}`}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ease-in-out ${
                  preset === p.key ? "bg-[#C8102E] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {preset === "custom" && (
            <div className="flex flex-wrap items-end gap-3" data-testid="dashboard-custom-range">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#94A3B8] block mb-1.5">From</label>
                <Input
                  type="date"
                  value={customFrom}
                  max={customTo || todayIso()}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="h-10 w-44 border-[#E2E8F0]"
                  data-testid="dashboard-date-from"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#94A3B8] block mb-1.5">To</label>
                <Input
                  type="date"
                  value={customTo}
                  min={customFrom}
                  max={todayIso()}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="h-10 w-44 border-[#E2E8F0]"
                  data-testid="dashboard-date-to"
                />
              </div>
              <button
                type="button"
                onClick={applyCustomRange}
                disabled={!customFrom || !customTo}
                className="h-10 px-4 rounded-md bg-[#0F172A] hover:bg-[#1E293B] disabled:opacity-50 text-white text-sm font-semibold transition-colors"
                data-testid="dashboard-apply-range"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {loading || !stats ? (
        <DashboardSkeleton />
      ) : (
        <>
          {(() => {
            const { kpis, overview, trend, section_stats: sectionStats, low_attendance_students: lowList, thresholds } = stats;
            const semesterLabel = `${thresholds?.semester_days ?? 120} days ending ${stats.filter?.date_to ?? ""}`;

            return (
              <>
                <div className="border-t border-l border-[#E2E8F0]">
                  <div className="px-6 md:px-12 py-4 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Key metrics · {filterLabel}</h2>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 bg-white">
                    <StatBlock label="Total Students" value={kpis.total_students} icon={Users} testid="stat-total-students" />
                    <StatBlock label="Present" value={kpis.present} sub="In selected period" icon={UserCheck} testid="stat-present-today" accent="text-[#059669]" />
                    <StatBlock label="Absent" value={kpis.absent} sub="In selected period" icon={UserX} testid="stat-absent-today" accent="text-[#E11D48]" />
                    <StatBlock label="Attendance %" value={`${kpis.attendance_pct}%`} sub="Selected period" icon={TrendingUp} testid="stat-overall-pct" accent="text-[#059669]" />
                    <StatBlock label="Classes Conducted" value={kpis.classes_conducted} sub="Closed sessions" icon={GraduationCap} testid="stat-classes-conducted" />
                    <StatBlock label="Attendance Pending" value={kpis.attendance_pending} sub="Only shown for today" icon={Clock} testid="stat-attendance-pending" accent="text-[#D97706]" />
                    <StatBlock
                      label="Low Attendance"
                      value={kpis.low_attendance_students}
                      sub={`Below ${thresholds?.low_attendance_pct ?? 75}% in period`}
                      icon={AlertTriangle}
                      testid="stat-low-attendance"
                      accent="text-[#D97706]"
                    />
                    <StatBlock label="Records" value={kpis.total_attendance_records} sub="In selected period" icon={Database} testid="stat-total-records" />
                  </div>
                  {kpis.active_sessions > 0 && (
                    <div className="px-6 md:px-12 py-3 bg-[#FDECEF] border-t border-[#E2E8F0] flex items-center gap-2 text-sm text-[#C8102E] font-semibold" data-testid="stat-active-sessions">
                      <Radio className="w-4 h-4" />
                      {kpis.active_sessions} live session{kpis.active_sessions !== 1 ? "s" : ""} in progress
                    </div>
                  )}
                </div>

                <div className="px-6 md:px-12 py-10 border-b border-[#E2E8F0]">
                  <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A] mb-1">Attendance overview</h2>
                  <p className="text-sm text-[#475569] mb-6">Present rate by window (ending {stats.filter?.date_to})</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <OverviewCard label={periodLabel} pct={overview.period.pct} present={overview.period.present} total={overview.period.total} testid="stat-period" />
                    <OverviewCard label="Weekly" pct={overview.weekly.pct} present={overview.weekly.present} total={overview.weekly.total} testid="stat-week" />
                    <OverviewCard label="Monthly" pct={overview.monthly.pct} present={overview.monthly.present} total={overview.monthly.total} testid="stat-month" />
                    <OverviewCard label="Semester" pct={overview.semester.pct} present={overview.semester.present} total={overview.semester.total} testid="stat-semester" />
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-3">Semester window: {semesterLabel}</p>
                </div>

                <div className="px-6 md:px-12 py-10 border-b border-[#E2E8F0]">
                  <div className="flex items-baseline justify-between mb-6">
                    <div>
                      <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A]">Attendance trend</h2>
                      <p className="text-sm text-[#475569] mt-1">{trendLabel}</p>
                    </div>
                    <button
                      onClick={() => navigate("/sessions")}
                      className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-[#C8102E] hover:text-[#9F0D24] transition-colors"
                      data-testid="go-sessions-btn"
                    >
                      View sessions <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="h-72 bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="attendance-chart">
                    {trend.length === 0 ? (
                      <div className="h-full flex items-center justify-center text-sm text-[#94A3B8]">No attendance data for this period.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trend} margin={{ top: 10, right: 20, bottom: 10, left: -10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#475569" }} tickFormatter={(d) => d.slice(5)} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#475569" }} unit="%" />
                          <Tooltip
                            contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }}
                            formatter={(v) => [`${v}%`, "Attendance"]}
                            labelFormatter={(d) => d}
                          />
                          <Line type="monotone" dataKey="pct" stroke="#C8102E" strokeWidth={2.5} dot={trend.length <= 14} activeDot={{ r: 5 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                <div className="px-6 md:px-12 py-10">
                  <div className="flex items-baseline justify-between mb-6">
                    <div>
                      <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A]">Section-wise attendance</h2>
                      <p className="text-sm text-[#475569] mt-1">For {filterLabel}</p>
                    </div>
                    <button onClick={() => navigate("/sections")} className="text-xs font-semibold text-[#C8102E] hover:text-[#9F0D24]" data-testid="dash-open-sections">
                      See all sections
                    </button>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    <div className="h-72 bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="section-compare-chart">
                      {sectionStats.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-sm text-[#94A3B8]">No section data.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={sectionStats} margin={{ top: 10, right: 10, bottom: 10, left: -20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} tickFormatter={(n) => `Sec ${n}`} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#475569" }} unit="%" />
                            <Tooltip
                              contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }}
                              formatter={(v) => [`${v}%`, "Attendance"]}
                            />
                            <Bar dataKey="overall_pct" radius={[4, 4, 0, 0]}>
                              {sectionStats.map((s) => (
                                <Cell key={s.id} fill={pctBarColor(s.overall_pct)} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>

                    <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="dash-section-list">
                      <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC] text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">
                        <div className="col-span-3">Section</div>
                        <div className="col-span-2 text-right">Students</div>
                        <div className="col-span-2 text-right">Present</div>
                        <div className="col-span-2 text-right">Absent</div>
                        <div className="col-span-3 text-right">Attendance %</div>
                      </div>
                      {sectionStats.map((s, i) => (
                        <button
                          key={s.id}
                          onClick={() => navigate(`/sections/${s.id}`)}
                          className={`w-full text-left px-5 py-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center hover:bg-[#F1F5F9] transition-colors ${i > 0 ? "border-t border-[#E2E8F0]" : ""}`}
                          data-testid={`dash-section-${s.id}`}
                        >
                          <div className="md:col-span-3 font-semibold text-[#0F172A]">Section {s.name}</div>
                          <div className="md:col-span-2 md:text-right text-sm text-[#475569]">{s.student_count}</div>
                          <div className="md:col-span-2 md:text-right text-sm text-[#059669] font-semibold">{s.present}</div>
                          <div className="md:col-span-2 md:text-right text-sm text-[#E11D48] font-semibold">{s.absent}</div>
                          <div className={`md:col-span-3 md:text-right font-heading font-bold ${pctTextColor(s.overall_pct)}`}>{s.overall_pct}%</div>
                        </button>
                      ))}
                      {sectionStats.length === 0 && (
                        <div className="px-5 py-8 text-center text-[#94A3B8] text-sm">No sections yet.</div>
                      )}
                    </div>
                  </div>
                </div>

                {lowList.length > 0 && (
                  <div className="px-6 md:px-12 pb-10">
                    <div className="flex items-baseline justify-between mb-4">
                      <h2 className="font-heading text-xl font-bold tracking-tight text-[#0F172A]">Students needing attention</h2>
                      <button onClick={() => navigate("/students")} className="text-xs font-semibold text-[#C8102E] hover:text-[#9F0D24]">
                        All students
                      </button>
                    </div>
                    <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
                      {lowList.map((s, i) => (
                        <button
                          key={s.qid}
                          onClick={() => navigate(`/students/${s.qid}`)}
                          className={`w-full text-left px-5 py-3 flex items-center gap-4 hover:bg-[#F1F5F9] transition-colors ${i > 0 ? "border-t border-[#E2E8F0]" : ""}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-[#0F172A]">{s.name || s.qid}</div>
                            <div className="text-xs text-[#94A3B8]">Roll {s.roll_no} · {s.present}/{s.total} sessions</div>
                          </div>
                          <div className={`font-heading font-bold ${pctTextColor(s.pct)}`}>{s.pct}%</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
